import Spacing from 'components/Spacing';
import PageSection from 'components/PageSection';
import CoreCompanyDetails from './CompanyDetails/CoreCompanyDetails';
import PortalInformation from './CompanyDetails/PortalInformationForm';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import { Grid } from '@mui/material';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { lpCompany as rules } from 'further-ui/validations';
import ClassHoldingsOverview from './ClassHoldings/ClassHoldingsOverview';
import GridContainer from 'components/GridContainer';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHistory } from 'react-router-dom';
import {
  createLpCompanyEditRoute,
  createLpCompanyRevaluationsRoute,
} from 'adminConstants/routes';
import { useNotification } from 'hooks/ui/useNotification';
import HoldingClassHeader from './HoldingClassHeader';
import CompanyPerformanceOverview from './CompanyDetails/CompanyPerformanceOverview';
import ErrorBoundary from 'components/ErrorBoundary';
import { Divider } from '@mui/material';
import { Vehicle } from 'further-types/lp/vehicle';
import PageLoader from 'components/PageLoader';

type Props = {
  selectedVehicle: Vehicle;
  companyId?: string;
};

const CompanyForm: React.FC<Props> = ({ selectedVehicle, companyId }) => {
  const [newHoldingClasses, setNewHoldingClasses] = useState<boolean>(false);
  const { handleSubmit, setValue, getValues } =
    useFormContext<rules.CompanySchemaFormValues>();
  const history = useHistory();
  const notification = useNotification();

  const mutationOpts = useHandleTrpcMutation();

  const { mutateAsync: createCompany, isPending: isCreatingCompany } =
    api.lpCompany.create.useMutation(
      mutationOpts({
        successMessage: 'Company created successfully.',
      }),
    );
  const { mutate: updateCompany, isPending: isUpdatingCompany } =
    api.lpCompany.update.useMutation(
      mutationOpts({
        successMessage: 'Company updated successfully.',
      }),
    );

  const {
    data: assetPerformanceAggregations,
    isLoading: isAssetPerformanceLoading,
  } = api.asset.getAssetPerformance.useQuery(
    {
      assetId: companyId ?? '',
      vehicleId: selectedVehicle._id,
    },
    {
      enabled: !!companyId && !!selectedVehicle._id,
    },
  );

  const isEdit = !!companyId;

  const hasInvestments =
    (assetPerformanceAggregations?.totalInvestmentAmountLcy ?? 0) > 0;

  const onSubmit = async (data: rules.CompanySchemaFormValues) => {
    try {
      if (isEdit) {
        await updateCompany({
          companyId,
          ...data,
        });
      } else {
        const newCompany = await createCompany(data);
        if (newCompany) {
          history.push(createLpCompanyEditRoute(newCompany._id));
        }
      }
    } catch (error) {
      console.error('Failed to save company:', error);
    }
  };
  const onError = () => {
    const defaultError = 'Please fill in all required fields.';
    notification.error(defaultError);
  };

  const handleCancel = () => {
    setValue('newHoldingClasses', []);
    setNewHoldingClasses(false);
  };

  const handleAddHoldingClass = () => {
    setValue('newHoldingClasses', [
      {
        name: '',
        assetId: undefined,
        currency: getValues('baseCurrency'),
        monitoringRights: undefined,
        liquidationPreferenceType: undefined,
        liquidationPreferenceMultiple: undefined,
        antiDilutionRights: undefined,
        couponRights: undefined,
        couponAmount: undefined,
      },
    ]);
    setNewHoldingClasses(true);
  };

  const handleRevalueClick = () => {
    if (companyId) {
      history.push(createLpCompanyRevaluationsRoute(companyId));
    }
  };

  if (isAssetPerformanceLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <Spacing>
        <form>
          <Spacing>
            <Spacing>
              <PageSection title="Core company details" collapsible>
                <GridContainer>
                  <Grid size={{ xs: 12, md: 12, lg: 8 }}>
                    <CoreCompanyDetails selectedVehicle={selectedVehicle} />
                  </Grid>
                </GridContainer>
              </PageSection>
              <PageSection title="Portal information" showDivider collapsible>
                <Spacing>
                  <GridContainer>
                    <Grid size={{ xs: 12, md: 12, lg: 8 }}>
                      <PortalInformation />
                    </Grid>
                  </GridContainer>
                  <GridContainer>
                    <Grid size={{ xs: 12 }}>
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          color="primary"
                          loading={isCreatingCompany || isUpdatingCompany}
                          onClick={handleSubmit(onSubmit, onError)}
                        >
                          Save
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </GridContainer>
                </Spacing>
              </PageSection>
              {companyId && assetPerformanceAggregations && (
                <>
                  <Divider />
                  <PageSection title="Performance">
                    <ErrorBoundary>
                      <CompanyPerformanceOverview
                        selectedVehicle={selectedVehicle}
                        assetPerformanceAggregations={
                          assetPerformanceAggregations
                        }
                      />
                    </ErrorBoundary>
                  </PageSection>
                </>
              )}
            </Spacing>

            {isEdit ? (
              <>
                <HoldingClassHeader
                  handleAddHoldingClass={handleAddHoldingClass}
                  newHoldingClasses={newHoldingClasses}
                  handleCancel={handleCancel}
                  onRevalueClick={handleRevalueClick}
                  hasInvestments={hasInvestments}
                />

                <PageSection>
                  <ClassHoldingsOverview
                    newHoldingClasses={newHoldingClasses}
                    vehicleId={selectedVehicle._id}
                    companyId={companyId}
                    onRevalueClick={handleRevalueClick}
                    setNewHoldingClasses={setNewHoldingClasses}
                    vehicleCurrencyCode={selectedVehicle.baseCurrency}
                  />
                </PageSection>
              </>
            ) : null}
          </Spacing>
        </form>
      </Spacing>
    </>
  );
};

export default CompanyForm;
